import { useCallback, useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useAppStore } from '../../../store/appStore';
import type { CreateMatterDto, MatterDetailed } from '../../../types/matters';

interface MatterLifecycleWorkflowPayload {
  matterId: string;
  realmId: string;
  triggerEvent: 'stage_transition' | 'milestone_reached' | 'risk_detected' | 'deadline_approaching' | 'manual_review';
  currentStage: string;
  targetStage?: string;
  newDocuments?: unknown[];
  deadlineDate?: string;
  riskFactors?: string[];
}

interface MatterRiskAssessmentWorkflowPayload {
  matterId: string;
  realmId: string;
  assessmentType: 'comprehensive' | 'document_based' | 'deadline_based' | 'financial' | 'strategic' | 'routine';
  triggerEvent: 'new_document' | 'deadline_approaching' | 'adverse_event' | 'stage_change' | 'periodic_review' | 'manual_request';
  riskThreshold?: number;
  includeFinancialRisk?: boolean;
  includeComplianceRisk?: boolean;
}

interface MCPWorkflowResponse {
  workflowId: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  result?: unknown;
  error?: string;
}

interface EngagementResponse {
  id: string;
  realmId: string;
  clientId: string;
  engagementNumber: string;
  title: string;
  summary?: string | null;
  status: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  estimatedValue?: number | null;
  isArchived: boolean;
  createdByUserId: string;
  responsibleStaffId?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isValidUuid = (value?: string | null): value is string =>
  typeof value === 'string' && UUID_PATTERN.test(value.trim());

function toMatterStatus(status: string): MatterDetailed['status'] {
  if (status === 'closed' || status === 'rejected') return 'closed';
  if (status === 'active') return 'active';
  return 'pending';
}

function toEngagementStatus(status?: string): 'intake' | 'review' | 'approved' | 'active' | 'closed' | 'rejected' | undefined {
  if (!status) return undefined;
  if (status === 'pending') return 'review';
  if (status === 'on_hold') return 'review';
  if (status === 'completed') return 'closed';
  if (['intake', 'review', 'approved', 'active', 'closed', 'rejected'].includes(status)) {
    return status as 'intake' | 'review' | 'approved' | 'active' | 'closed' | 'rejected';
  }
  return undefined;
}

function toMatterDetailed(engagement: EngagementResponse): MatterDetailed {
  const status = toMatterStatus(engagement.status);
  return {
    id: engagement.id,
    realm_id: engagement.realmId,
    client_id: engagement.clientId,
    matter_number: engagement.engagementNumber,
    displayNumber: engagement.engagementNumber,
    title: engagement.title,
    description: engagement.title,
    status,
    stage: engagement.status,
    practice_area: 'General',
    custom_fields: {},
    created_at: engagement.createdAt,
    updated_at: engagement.updatedAt,
    estimated_value: engagement.estimatedValue ?? undefined,
    responsibleAttorney: { id: engagement.responsibleStaffId || '', name: 'Unassigned' },
    originatingAttorney: { id: engagement.createdByUserId, name: 'Unassigned' },
    responsibleStaff: engagement.responsibleStaffId ? { id: engagement.responsibleStaffId, name: 'Assigned' } : null,
    openDate: engagement.createdAt,
    closeDate: status === 'closed' ? engagement.updatedAt : undefined,
    responsible_staff_id: engagement.responsibleStaffId || null,
    access_level: 'everyone',
    allowed_user_ids: [],
    allowed_group_ids: [],
    blocked_user_ids: [],
    priority: engagement.priority,
    matter_summary: engagement.summary || null,
    important_dates: null,
    related_proceedings: [],
    related_proceeding_summary: {
      total: 0,
      linked_matters: 0,
      external_only: 0,
      cross_jurisdiction: 0,
      affects_deadlines: 0,
      affects_conflicts: 0,
    },
    permissions: [],
    customRates: [],
    billingMethod: 'Hourly',
    isBillable: true,
    notes: [],
    documents: [],
    communications: [],
    bills: [],
    transactions: [],
    tasks: [],
    timeline: [],
    workInProgress: 0,
    outstandingBalance: 0,
    trustFunds: 0,
  };
}

function mapCreateMatterToEngagement(matterData: CreateMatterDto) {
  return {
    clientId: matterData.client_id,
    title: matterData.description || matterData.matter_number || 'New Engagement',
    summary: matterData.description || null,
    engagementNumber: matterData.matter_number || undefined,
    priority: matterData.priority,
    estimatedValue: matterData.estimated_value,
    status: toEngagementStatus((matterData as { status?: string }).status) || 'active',
  };
}

function mapUpdateMatterToEngagement(updates: Record<string, unknown>) {
  return {
    clientId: typeof updates.client_id === 'string' ? updates.client_id : undefined,
    title: typeof updates.description === 'string' ? updates.description : undefined,
    summary: typeof updates.matter_summary === 'string' || updates.matter_summary === null ? updates.matter_summary : undefined,
    engagementNumber: typeof updates.matter_number === 'string' ? updates.matter_number : undefined,
    priority: typeof updates.priority === 'string' ? updates.priority : undefined,
    estimatedValue: typeof updates.estimated_value === 'number' || updates.estimated_value === null ? updates.estimated_value : undefined,
    status: toEngagementStatus(
      typeof updates.status === 'string'
        ? updates.status
        : typeof updates.stage === 'string'
          ? updates.stage
          : undefined,
    ),
  };
}

async function parseEnvelope<T>(response: Response): Promise<T> {
  const envelope = await response.json() as ApiEnvelope<T>;
  if (!response.ok || envelope.success === false || !envelope.data) {
    throw new Error(envelope.error || `Engagement request failed with ${response.status}`);
  }
  return envelope.data;
}

function unsupportedEngagementRuntimeSurface(name: string): never {
  throw new Error(`${name} has not been forked to the WeBelieve engagement API yet.`);
}

export const useMatters = () => {
  const { keycloak } = useKeycloak();
  const {
    matters,
    setMatters,
    setMattersLoading,
    setMattersError,
  } = useAppStore();

  const [workflowStatus, setWorkflowStatus] = useState<Record<string, string>>({});

  const getRealmFromToken = useCallback((): string | null => {
    if (!keycloak?.token) return null;

    try {
      const payload = JSON.parse(atob(keycloak.token.split('.')[1]));
      const realmMatch = payload.iss?.match(/\/realms\/([^/]+)$/);
      return realmMatch ? realmMatch[1] : null;
    } catch {
      return null;
    }
  }, [keycloak?.token]);

  const validateRealmContext = useCallback((expectedRealm?: string): void => {
    const tokenRealm = getRealmFromToken();
    if (!tokenRealm) {
      throw new Error('Cannot validate realm context because no realm was extracted from the token.');
    }
    if (expectedRealm && tokenRealm !== expectedRealm) {
      throw new Error(`Realm mismatch: expected ${expectedRealm}, got ${tokenRealm}`);
    }
  }, [getRealmFromToken]);

  const getCurrentRealm = useCallback((): string => {
    const realm = getRealmFromToken();
    if (!realm) throw new Error('Cannot determine current realm from JWT token');
    return realm;
  }, [getRealmFromToken]);

  const getAuthHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${keycloak?.token || ''}`,
  }), [keycloak?.token]);

  const validateMatterBelongsToRealm = useCallback((matter: MatterDetailed): void => {
    const currentRealm = getCurrentRealm();
    if (matter.realm_id !== currentRealm) {
      throw new Error(`Engagement ${matter.id} does not belong to current realm ${currentRealm}`);
    }
  }, [getCurrentRealm]);

  const fetchMatters = useCallback(async (filters?: {
    status?: string;
    practice_area?: string;
    assigned_attorney_id?: string;
    client_id?: string;
    clientId?: string;
    search?: string;
  }) => {
    try {
      setMattersLoading(true);
      setMattersError(null);

      const params = new URLSearchParams();
      const clientId = filters?.clientId || filters?.client_id;
      if (clientId) params.set('clientId', clientId);

      const response = await fetch(`/api/v1/engagements?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      const data = await parseEnvelope<{ engagements: EngagementResponse[] }>(response);
      let nextMatters = data.engagements.map(toMatterDetailed);

      if (filters?.status) {
        nextMatters = nextMatters.filter((matter) => matter.status === filters.status || matter.stage === filters.status);
      }
      if (filters?.search) {
        const needle = filters.search.toLowerCase();
        nextMatters = nextMatters.filter((matter) => (
          matter.title.toLowerCase().includes(needle)
          || matter.matter_number.toLowerCase().includes(needle)
        ));
      }

      setMatters(nextMatters);
      return nextMatters;
    } catch (error) {
      setMattersError(error instanceof Error ? error.message : 'Failed to fetch engagements');
      throw error;
    } finally {
      setMattersLoading(false);
    }
  }, [getAuthHeaders, setMatters, setMattersError, setMattersLoading]);

  const fetchMatterById = useCallback(async (matterId: string): Promise<MatterDetailed | null> => {
    if (!isValidUuid(matterId)) return null;

    try {
      const response = await fetch(`/api/v1/engagements/${matterId}`, {
        headers: getAuthHeaders(),
      });
      if (response.status === 404) return null;
      const data = await parseEnvelope<{ engagement: EngagementResponse }>(response);
      return toMatterDetailed(data.engagement);
    } catch (error) {
      setMattersError(error instanceof Error ? error.message : 'Failed to fetch engagement');
      throw error;
    }
  }, [getAuthHeaders, setMattersError]);

  const createMatter = useCallback(async (matterData: CreateMatterDto) => {
    try {
      setMattersLoading(true);
      const response = await fetch('/api/v1/engagements', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(mapCreateMatterToEngagement(matterData)),
      });
      const data = await parseEnvelope<{ engagement: EngagementResponse }>(response);
      const newMatter = toMatterDetailed(data.engagement);
      setMatters([...matters, newMatter]);
      return newMatter;
    } catch (error) {
      setMattersError(error instanceof Error ? error.message : 'Failed to create engagement');
      throw error;
    } finally {
      setMattersLoading(false);
    }
  }, [getAuthHeaders, matters, setMatters, setMattersError, setMattersLoading]);

  const updateMatter = useCallback(async (matterId: string, updates: Record<string, unknown>) => {
    try {
      setMattersLoading(true);
      const response = await fetch(`/api/v1/engagements/${matterId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(mapUpdateMatterToEngagement(updates)),
      });
      const data = await parseEnvelope<{ engagement: EngagementResponse }>(response);
      const updatedMatter = toMatterDetailed(data.engagement);
      setMatters(matters.map((matter) => (matter.id === matterId ? updatedMatter : matter)));
      return updatedMatter;
    } catch (error) {
      setMattersError(error instanceof Error ? error.message : 'Failed to update engagement');
      throw error;
    } finally {
      setMattersLoading(false);
    }
  }, [getAuthHeaders, matters, setMatters, setMattersError, setMattersLoading]);

  const deleteMatter = useCallback(async (matterId: string) => {
    try {
      setMattersLoading(true);
      const response = await fetch(`/api/v1/engagements/${matterId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      await parseEnvelope<{ deleted: boolean }>(response);
      setMatters(matters.filter((matter) => matter.id !== matterId));
      return true;
    } catch (error) {
      setMattersError(error instanceof Error ? error.message : 'Failed to delete engagement');
      throw error;
    } finally {
      setMattersLoading(false);
    }
  }, [getAuthHeaders, matters, setMatters, setMattersError, setMattersLoading]);

  const executeStageTransition = useCallback(async (matterId: string, targetStage: string) => {
    const updated = await updateMatter(matterId, { stage: targetStage });
    return { success: true, engagement: updated };
  }, [updateMatter]);

  const executeMatterLifecycleWorkflow = useCallback(async (_payload: MatterLifecycleWorkflowPayload): Promise<MCPWorkflowResponse> => {
    setWorkflowStatus((current) => current);
    return unsupportedEngagementRuntimeSurface('Engagement lifecycle workflow');
  }, []);

  const executeRiskAssessmentWorkflow = useCallback(async (_payload: MatterRiskAssessmentWorkflowPayload): Promise<MCPWorkflowResponse> => (
    unsupportedEngagementRuntimeSurface('Engagement risk assessment workflow')
  ), []);

  const triggerRiskAssessment = useCallback(async (_matterId: string) => (
    unsupportedEngagementRuntimeSurface('Engagement risk assessment')
  ), []);

  const getWorkflowStatus = useCallback(async (_workflowId: string) => (
    unsupportedEngagementRuntimeSurface('Engagement workflow status')
  ), []);

  const getMatterProgress = useCallback(async (_matterId: string) => (
    unsupportedEngagementRuntimeSurface('Engagement progress metrics')
  ), []);

  const getMatterRisk = useCallback(async (_matterId: string) => (
    unsupportedEngagementRuntimeSurface('Engagement risk metrics')
  ), []);

  const getMatterTimeline = useCallback(async (_matterId: string) => (
    unsupportedEngagementRuntimeSurface('Engagement timeline metrics')
  ), []);

  const getMatterPerformance = useCallback(async (_matterId: string) => (
    unsupportedEngagementRuntimeSurface('Engagement performance metrics')
  ), []);

  return {
    matters,
    workflowStatus,
    fetchMatters,
    fetchMatterById,
    createMatter,
    updateMatter,
    deleteMatter,
    executeStageTransition,
    triggerRiskAssessment,
    executeMatterLifecycleWorkflow,
    executeRiskAssessmentWorkflow,
    getWorkflowStatus,
    getMatterProgress,
    getMatterRisk,
    getMatterTimeline,
    getMatterPerformance,
    getRealmFromToken,
    validateRealmContext,
    getCurrentRealm,
    validateMatterBelongsToRealm,
    loading: useAppStore((state) => state.mattersLoading),
    error: useAppStore((state) => state.mattersError),
  };
};
